import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BarChart3, TrendingUp, Clock, Brain, Target, MessageSquare, Activity, Trophy, RefreshCw, X, Maximize2, Minimize2, Plus } from 'lucide-react';

interface ReadingSession {
  id: string;
  articleId: string;
  articleTitle: string;
  startTime: Date;
  endTime: Date;
  duration: number; // in seconds
  wordsRead: number;
  wordsPerMinute: number;
  comprehensionScore: number;
  highlightsCount: number;
  annotationsCount: number;
  sharesCount: number;
  likesCount: number;
  deviceType: 'desktop' | 'mobile' | 'tablet';
  readingMode: 'normal' | 'skim' | 'deep' | 'speed';
  environment: 'light' | 'dark';
  audioEnabled: boolean;
  ttsUsed: boolean;
  focusMode: boolean;
  interruptions: number;
  scrollDepth: number; // percentage
  timeSpentOnImages: number;
  timeSpentOnVideos: number;
  timeSpentOnLinks: number;
  searchQueries: string[];
  bookmarksCreated: number;
  notesCreated: number;
  socialInteractions: number;
}

interface ReadingInsight {
  id: string;
  type: 'speed' | 'comprehension' | 'engagement' | 'habits' | 'recommendations';
  title: string;
  description: string;
  value: number;
  trend: 'up' | 'down' | 'stable';
  confidence: number;
  actionable: boolean;
  actionText?: string;
  onAction?: () => void;
  createdAt: Date;
}

interface ReadingGoal {
  id: string;
  title: string;
  type: 'daily' | 'weekly' | 'monthly' | 'custom';
  target: number;
  current: number;
  unit: 'minutes' | 'articles' | 'words' | 'pages';
  startDate: Date;
  endDate: Date;
  isActive: boolean;
  streak: number;
  bestStreak: number;
  lastAchieved: Date;
}

interface ReadingAnalyticsProps {
  userId: string;
  onInsightAction?: (insight: ReadingInsight) => void;
  onGoalUpdate?: (goal: ReadingGoal) => void;
  className?: string;
}

const ReadingAnalytics: React.FC<ReadingAnalyticsProps> = ({
  userId,
  onInsightAction,
  onGoalUpdate,
  className = ''
}) => {
  const [sessions, setSessions] = useState<ReadingSession[]>([]);
  const [insights, setInsights] = useState<ReadingInsight[]>([]);
  const [goals, setGoals] = useState<ReadingGoal[]>([]);
  const [selectedTimeframe, setSelectedTimeframe] = useState<'7d' | '30d' | '90d' | '1y'>('30d');
  const [selectedMetric, setSelectedMetric] = useState<'speed' | 'comprehension' | 'engagement' | 'habits'>('speed');
  const [showGoals, setShowGoals] = useState(true);
  const [showInsights, setShowInsights] = useState(true);
  const [showDetailedView, setShowDetailedView] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Mock data generation
  useEffect(() => {
    const mockSessions: ReadingSession[] = [
      {
        id: '1',
        articleId: 'article-1',
        articleTitle: 'The Future of Web Development',
        startTime: new Date(Date.now() - 2 * 60 * 60 * 1000),
        endTime: new Date(Date.now() - 1.5 * 60 * 60 * 1000),
        duration: 1800, // 30 minutes
        wordsRead: 2500,
        wordsPerMinute: 83,
        comprehensionScore: 0.85,
        highlightsCount: 12,
        annotationsCount: 8,
        sharesCount: 3,
        likesCount: 7,
        deviceType: 'desktop',
        readingMode: 'deep',
        environment: 'dark',
        audioEnabled: false,
        ttsUsed: false,
        focusMode: true,
        interruptions: 2,
        scrollDepth: 95,
        timeSpentOnImages: 120,
        timeSpentOnVideos: 0,
        timeSpentOnLinks: 45,
        searchQueries: ['react server components', 'webassembly'],
        bookmarksCreated: 2,
        notesCreated: 5,
        socialInteractions: 10
      },
      {
        id: '2',
        articleId: 'article-2',
        articleTitle: 'AI in Modern Development',
        startTime: new Date(Date.now() - 4 * 60 * 60 * 1000),
        endTime: new Date(Date.now() - 3.5 * 60 * 60 * 1000),
        duration: 1800,
        wordsRead: 1800,
        wordsPerMinute: 90,
        comprehensionScore: 0.78,
        highlightsCount: 8,
        annotationsCount: 4,
        sharesCount: 1,
        likesCount: 3,
        deviceType: 'mobile',
        readingMode: 'skim',
        environment: 'light',
        audioEnabled: true,
        ttsUsed: true,
        focusMode: false,
        interruptions: 5,
        scrollDepth: 80,
        timeSpentOnImages: 60,
        timeSpentOnVideos: 180,
        timeSpentOnLinks: 30,
        searchQueries: ['machine learning', 'neural networks'],
        bookmarksCreated: 1,
        notesCreated: 2,
        socialInteractions: 5
      }
    ];

    const mockInsights: ReadingInsight[] = [
      {
        id: '1',
        type: 'speed',
        title: 'Reading Speed Improved',
        description: 'Your average reading speed has increased by 15% this month',
        value: 15,
        trend: 'up',
        confidence: 0.92,
        actionable: true,
        actionText: 'Set a new speed goal',
        createdAt: new Date()
      },
      {
        id: '2',
        type: 'comprehension',
        title: 'Deep Reading Mode Effective',
        description: 'You retain 23% more information when using deep reading mode',
        value: 23,
        trend: 'up',
        confidence: 0.88,
        actionable: true,
        actionText: 'Enable deep reading by default',
        createdAt: new Date()
      },
      {
        id: '3',
        type: 'engagement',
        title: 'High Engagement with Tech Articles',
        description: 'You spend 40% more time on technology-related content',
        value: 40,
        trend: 'up',
        confidence: 0.95,
        actionable: false,
        createdAt: new Date()
      },
      {
        id: '4',
        type: 'habits',
        title: 'Consistent Reading Schedule',
        description: 'You read most consistently between 7-9 PM',
        value: 85,
        trend: 'stable',
        confidence: 0.90,
        actionable: true,
        actionText: 'Set reading reminders',
        createdAt: new Date()
      }
    ];

    const mockGoals: ReadingGoal[] = [
      {
        id: '1',
        title: 'Daily Reading Goal',
        type: 'daily',
        target: 30,
        current: 25,
        unit: 'minutes',
        startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
        endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        isActive: true,
        streak: 12,
        bestStreak: 18,
        lastAchieved: new Date()
      },
      {
        id: '2',
        title: 'Weekly Article Goal',
        type: 'weekly',
        target: 10,
        current: 7,
        unit: 'articles',
        startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        isActive: true,
        streak: 3,
        bestStreak: 8,
        lastAchieved: new Date()
      }
    ];

    setSessions(mockSessions);
    setInsights(mockInsights);
    setGoals(mockGoals);
  }, [userId]);

  const generateInsights = async () => {
    setIsGenerating(true);
    
    // Simulate AI analysis
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const newInsights: ReadingInsight[] = [
      {
        id: Date.now().toString(),
        type: 'recommendations',
        title: 'Optimize Reading Environment',
        description: 'Try reading in dark mode during evening hours for better focus',
        value: 0,
        trend: 'stable',
        confidence: 0.75,
        actionable: true,
        actionText: 'Enable dark mode',
        createdAt: new Date()
      }
    ];
    
    setInsights(prev => [...prev, ...newInsights]);
    setIsGenerating(false);
  };

  const updateGoal = (goalId: string, updates: Partial<ReadingGoal>) => {
    setGoals(prev => prev.map(goal => 
      goal.id === goalId 
        ? { ...goal, ...updates }
        : goal
    ));
    
    const updatedGoal = goals.find(g => g.id === goalId);
    if (updatedGoal) {
      onGoalUpdate?.({ ...updatedGoal, ...updates });
    }
  };

  const handleInsightAction = (insight: ReadingInsight) => {
    onInsightAction?.(insight);
  };

  // Calculate statistics
  const totalSessions = sessions.length;
  const totalReadingTime = sessions.reduce((sum, session) => sum + session.duration, 0);
  const averageWPM = sessions.reduce((sum, session) => sum + session.wordsPerMinute, 0) / sessions.length;
  const averageComprehension = sessions.reduce((sum, session) => sum + session.comprehensionScore, 0) / sessions.length;
  const totalHighlights = sessions.reduce((sum, session) => sum + session.highlightsCount, 0);
  const totalAnnotations = sessions.reduce((sum, session) => sum + session.annotationsCount, 0);

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'down': return <TrendingUp className="w-4 h-4 text-red-500 rotate-180" />;
      default: return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  const getInsightColor = (type: string) => {
    switch (type) {
      case 'speed': return 'text-blue-600 bg-blue-100 dark:bg-blue-900 dark:text-blue-400';
      case 'comprehension': return 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-400';
      case 'engagement': return 'text-purple-600 bg-purple-100 dark:bg-purple-900 dark:text-purple-400';
      case 'habits': return 'text-orange-600 bg-orange-100 dark:bg-orange-900 dark:text-orange-400';
      case 'recommendations': return 'text-indigo-600 bg-indigo-100 dark:bg-indigo-900 dark:text-indigo-400';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  return (
    <div className={`reading-analytics ${className}`}>
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-blue-500" />
              Reading Analytics
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Track your reading habits and improve your learning
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={generateInsights}
              disabled={isGenerating}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
            >
              {isGenerating ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Brain className="w-4 h-4" />
                  Generate Insights
                </>
              )}
            </button>
            
            <button
              onClick={() => setShowDetailedView(!showDetailedView)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              {showDetailedView ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {totalSessions}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Sessions</div>
          </div>
          
          <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {formatDuration(totalReadingTime)}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Time</div>
          </div>
          
          <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {Math.round(averageWPM)}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Avg WPM</div>
          </div>
          
          <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {Math.round(averageComprehension * 100)}%
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Comprehension</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Reading Goals */}
        {showGoals && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Target className="w-5 h-5 text-green-500" />
                Reading Goals
              </h3>
              <button
                onClick={() => setShowGoals(!showGoals)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <div className="space-y-4">
              {goals.map(goal => (
                <div key={goal.id} className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-900 dark:text-white">{goal.title}</h4>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">
                        {goal.current}/{goal.target} {goal.unit}
                      </span>
                      <div className="flex items-center gap-1">
                        <Trophy className="w-4 h-4 text-yellow-500" />
                        <span className="text-sm font-medium">{goal.streak}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(goal.current / goal.target) * 100}%` }}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                    <span>Best streak: {goal.bestStreak}</span>
                    <span>{Math.round((goal.current / goal.target) * 100)}% complete</span>
                  </div>
                </div>
              ))}
              
              <button className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-gray-400 dark:hover:border-gray-500 transition-colors">
                <Plus className="w-4 h-4" />
                <span className="text-gray-600 dark:text-gray-400">Add New Goal</span>
              </button>
            </div>
          </div>
        )}

        {/* AI Insights */}
        {showInsights && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-500" />
                AI Insights
              </h3>
              <button
                onClick={() => setShowInsights(!showInsights)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <div className="space-y-3">
              {insights.map(insight => (
                <div key={insight.id} className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${getInsightColor(insight.type)}`}>
                      {getTrendIcon(insight.trend)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-gray-900 dark:text-white">{insight.title}</h4>
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                          {insight.type}
                        </span>
                        <span className="text-xs text-gray-500">
                          {Math.round(insight.confidence * 100)}% confidence
                        </span>
                      </div>
                      
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {insight.description}
                      </p>
                      
                      {insight.actionable && insight.actionText && (
                        <button
                          onClick={() => handleInsightAction(insight)}
                          className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                        >
                          {insight.actionText} →
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reading Sessions */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-blue-500" />
            Recent Sessions
          </h3>
          
          <div className="space-y-3">
            {sessions.map(session => (
              <div key={session.id} className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                    {session.articleTitle}
                  </h4>
                  <span className="text-xs text-gray-500">
                    {formatDate(session.startTime)}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-400">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>{formatDuration(session.duration)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    <span>{session.wordsPerMinute} WPM</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Brain className="w-4 h-4" />
                    <span>{Math.round(session.comprehensionScore * 100)}%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    <span>{session.annotationsCount} notes</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 mt-2">
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    session.deviceType === 'desktop' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400' :
                    session.deviceType === 'mobile' ? 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400' :
                    'bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-400'
                  }`}>
                    {session.deviceType}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    session.readingMode === 'deep' ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900 dark:text-indigo-400' :
                    session.readingMode === 'skim' ? 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900 dark:text-yellow-400' :
                    'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                  }`}>
                    {session.readingMode}
                  </span>
                  {session.focusMode && (
                    <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-400">
                      focus
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Detailed Analytics */}
        {showDetailedView && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-4">
              <Activity className="w-5 h-5 text-green-500" />
              Detailed Analytics
            </h3>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {totalHighlights}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Total Highlights</div>
                </div>
                
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {totalAnnotations}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Total Annotations</div>
                </div>
              </div>
              
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900 dark:text-white">Reading Patterns</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Most active device:</span>
                    <span className="font-medium">Desktop (60%)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Preferred reading mode:</span>
                    <span className="font-medium">Deep (45%)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Average session length:</span>
                    <span className="font-medium">25 minutes</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Focus mode usage:</span>
                    <span className="font-medium">70%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReadingAnalytics;
